import { memo, useCallback, useRef, useEffect, useMemo, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import { observer } from "mobx-react-lite";
import { reaction, untracked } from "mobx";
import { Tooltip } from "antd";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  FlagOutlined,
} from "@ant-design/icons";
import {
  closestCenter,
  pointerWithin,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type {
  CollisionDetection,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type {
  MotionMarkerPatch,
  MotionMoveLayersOptions,
  MotionStructuralApplyResult,
  MotionTargetQuery,
} from "@blings/blings-player/editor-tools";
import { useMst } from "../../stores/main";
import {
  setLayerInspectorSelections,
  setLayerInspectorEnabled,
  setLayerInspectorHover,
  clearLayerInspectorHover,
  buildLayerUidClassName,
  buildLayerNameClassName,
} from "../../helpers/layerInspectorTools";
import type { LayerInspectorSelection } from "../../helpers/layerInspectorTools";
import { recordStudioPerf, studioPerfNow } from "../../helpers/studioPerfDebug";
import {
  buildLayerBindingKeys,
  buildLayerInspectorKeySet,
  buildLayerInspectorSelectionKey,
  collectLayerBindings,
  getNextLayerInspectorSelections,
  layerMatchesInspectorKeySet,
} from "../../helpers/layerSelectionUtils";
import { isKeyboardShortcutSuppressedTarget } from "../../hooks/keyboardShortcutGuards";
import { useTimelineLayers } from "./hooks/useTimelineLayers";
import { useTimelineZoom } from "./hooks/useTimelineZoom";
import { useLayerKeyframeData } from "./hooks/useTrackHeights";
import { useAnimationJsonEdit } from "./hooks/useAnimationJsonEdit";
import { TREE_ROW_HEIGHT } from "./utils/keyframeHelpers";
import {
  applyKeyframeDeletionTargets,
  collectSelectedKeyframeDeletionTargets,
} from "./utils/keyframeDeletionHelpers";
import { TimelineControls } from "./TimelineControls";
import { TimelineRuler } from "./TimelineRuler";
import { TimelinePlayhead } from "./TimelinePlayhead";
import { TimelineWorkAreaOverlay } from "./TimelineWorkAreaOverlay";
import { TimelineSceneTrimBar } from "./TimelineSceneTrimBar";
import { TimelineTreeNodeLabel } from "./TimelineLayerLabel";
import { TimelineTreeNodeTrack, getResolvedLayerTiming } from "./TimelineTrack";
import type { SelectedLayerInfo } from "./hooks/useLayerBodyDrag";
import { getTimelineSeek } from "./utils/timelineSeek";
import type { TimelineSeekPlayer } from "./utils/timelineSeek";
import {
  buildTimelineLayerTree,
  flattenLayers,
  flattenTimelineTree,
  getDefaultExpandedPrecompKeys,
} from "./utils/layerHierarchy";
import {
  getTimelineMarqueeSelection,
  rectsIntersect,
} from "./utils/marqueeSelectionHelpers";
import { FrameIndicator } from "../Vid-Bottom-Ctrls/FrameIndicator";
import MuterSingleton from "../../utils/mute";
import { LayerType, modTypes } from "@blings/blings-player";
import { modsDefinitions } from "../mods/mod-definitions";
import type {
  TimelineLayerInfo,
  TimelineTreeNode,
} from "./utils/layerHierarchy";
import type { IModModel } from "../../stores/ModsStore";
import {
  SoundOnIcon,
  SoundOffIcon,
  KeyframeIcon,
  KeyframeFilledIcon,
  WorkareaIcon,
  WorkareaActiveIcon,
  AnimatedLayersIcon,
} from "./TimelineIcons";
import { clamp } from "./utils/timelineHelpers";
import "./TimelinePanel.scss";

const ANIMATE_MOD_TYPES = new Set([modTypes.transform, modTypes.custom]);

const KEYFRAME_SELECTION_MIN_DRAG_PX = 2;

function isTimelineDeleteKey(e: KeyboardEvent): boolean {
  return e.key === "Delete" || e.key === "Backspace";
}

type ModConnection = {
  modId: number;
  color: string;
};

const toLayerIdentity = (layer: TimelineLayerInfo) => ({
  instancePath: layer.instancePath,
  layerPath: layer.layerPath,
  layerUid: layer.uid,
  layerName: layer.nm,
  assetId: layer.assetId,
});

const buildMotionLayerTarget = (
  layer: TimelineLayerInfo
): MotionTargetQuery => ({
  path: layer.layerPath,
});

const getErrorLogPayload = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  return error;
};

const getBoundedCompositionFrame = (
  frame: number,
  compositionStart: number,
  compositionEnd: number
) =>
  clamp(
    Math.round(Number.isFinite(frame) ? frame : compositionStart),
    compositionStart,
    Math.max(compositionStart, compositionEnd)
  );

type LayerDropLinePosition = "before" | "after";

type LayerMoveOptions = Pick<
  MotionMoveLayersOptions,
  "container" | "order" | "reparent"
>;

type LayerDropIntent =
  | {
      kind: "insert";
      activeId: string;
      overId: string;
      placement: LayerDropLinePosition;
      containerNodeKey?: string;
      moveOptions: LayerMoveOptions;
    }
  | {
      kind: "inside";
      activeId: string;
      overId: string;
      containerNodeKey: string;
      moveOptions: LayerMoveOptions;
    };

// Helper to check if a layer overlaps with the workarea
const layerOverlapsWorkarea = (
  layer: TimelineLayerInfo,
  compositionStart: number,
  compositionEnd: number
): boolean => {
  return layer.ip < compositionEnd && layer.op > compositionStart;
};

const collectAnimatedTreeState = (
  layers: TimelineLayerInfo[],
  layerKeyframeData: Map<string, { hasKeyframes: boolean }>,
  visibleKeys: Set<string>,
  expandedKeys: Set<string>
): boolean => {
  let hasAnimatedLayer = false;

  for (const layer of layers) {
    const hasAnimatedChildren = layer.children
      ? collectAnimatedTreeState(
          layer.children,
          layerKeyframeData,
          visibleKeys,
          expandedKeys
        )
      : false;
    const hasOwnKeyframes =
      layerKeyframeData.get(layer.nodeKey)?.hasKeyframes ?? false;
    const shouldShowLayer = hasOwnKeyframes || hasAnimatedChildren;

    if (hasAnimatedChildren) {
      expandedKeys.add(layer.nodeKey);
    }

    if (shouldShowLayer) {
      visibleKeys.add(layer.nodeKey);
      hasAnimatedLayer = true;
    }
  }

  return hasAnimatedLayer;
};

const flattenAllLayers = (
  layers: TimelineLayerInfo[],
  depth: number = 0
): Array<{ layer: TimelineLayerInfo; depth: number }> => {
  const result: Array<{ layer: TimelineLayerInfo; depth: number }> = [];

  for (const layer of layers) {
    result.push({ layer, depth });

    if (layer.children?.length) {
      result.push(...flattenAllLayers(layer.children, depth + 1));
    }
  }

  return result;
};

const getLayerDropIntentKey = (intent: LayerDropIntent | null): string =>
  intent
    ? [
        intent.kind,
        intent.activeId,
        intent.overId,
        intent.kind === "insert" ? intent.placement : "inside",
        intent.containerNodeKey ?? "root",
      ].join(":")
    : "";

const isPrecompContainerLayer = (layer: TimelineLayerInfo): boolean =>
  layer.ty === LayerType.precomp && Boolean(layer.refId);

const getLayerListPath = (layer: TimelineLayerInfo): string =>
  layer.layerPath.replace(/\[\d+\]$/, "");

const hasParentReference = (layer: TimelineLayerInfo): boolean =>
  typeof layer.parent === "number";

const shouldDetachExternalParent = (
  draggedLayers: TimelineLayerInfo[],
  destinationLayer: TimelineLayerInfo
): boolean =>
  draggedLayers.some(
    (layer) =>
      hasParentReference(layer) &&
      getLayerListPath(layer) !== getLayerListPath(destinationLayer)
  );

const getLayerDropZone = (
  pointerY: number,
  overRect?: { top: number; height: number } | null
): LayerDropLinePosition | "inside" => {
  if (!overRect) {
    return "inside";
  }

  const rowOffset = pointerY - overRect.top;
  if (rowOffset < overRect.height * 0.25) {
    return "before";
  }
  if (rowOffset > overRect.height * 0.75) {
    return "after";
  }
  return "inside";
};

const isLayerCoveredByDraggedClosure = (
  layer: TimelineLayerInfo,
  draggedLayers: TimelineLayerInfo[]
): boolean =>
  draggedLayers.some(
    (draggedLayer) =>
      layer.instancePath === draggedLayer.instancePath ||
      layer.instancePath.startsWith(`${draggedLayer.instancePath}::`)
  );

const removeLayersCoveredByDraggedAncestor = (
  layers: TimelineLayerInfo[]
): TimelineLayerInfo[] =>
  layers.filter(
    (layer) =>
      !layers.some(
        (candidate) =>
          candidate !== layer &&
          layer.instancePath.startsWith(`${candidate.instancePath}::`)
      )
  );

const isLayerInSelectionScope = (
  layer: TimelineLayerInfo,
  containerInstancePath?: string
): boolean =>
  containerInstancePath
    ? layer.instancePath.startsWith(`${containerInstancePath}::`)
    : !layer.instancePath.includes("::");

const findMovedLayerInTree = (
  layers: TimelineLayerInfo[],
  sourceLayer: TimelineLayerInfo,
  nextPath: string | undefined,
  containerInstancePath: string | undefined
): TimelineLayerInfo | undefined => {
  const candidates = flattenAllLayers(layers)
    .map(({ layer }) => layer)
    .filter((layer) => isLayerInSelectionScope(layer, containerInstancePath));

  if (nextPath) {
    const pathMatches = candidates.filter(
      (layer) => layer.layerPath === nextPath
    );
    if (pathMatches.length === 1) {
      return pathMatches[0];
    }
  }

  const identityMatches = candidates.filter((layer) => {
    if (sourceLayer.uid !== undefined) {
      return layer.uid === sourceLayer.uid && layer.nm === sourceLayer.nm;
    }
    return layer.nm === sourceLayer.nm;
  });

  return identityMatches.length === 1 ? identityMatches[0] : undefined;
};

const buildMovedLayerSelections = (
  result: MotionStructuralApplyResult,
  sourceLayers: TimelineLayerInfo[],
  buildSelection: (layer: TimelineLayerInfo) => LayerInspectorSelection,
  containerInstancePath?: string
): LayerInspectorSelection[] => {
  const nextLayers = buildTimelineLayerTree(result.animation).layers;
  const movedChanges = result.pathChanges.filter(
    (change) => change.status === "moved" && change.newPath
  );
  const movedPathByOldPath = new Map(
    movedChanges
      .filter((change) => change.oldPath)
      .map((change) => [change.oldPath!, change.newPath!])
  );
  const selections = sourceLayers
    .map((layer) => {
      const exactMovedPath = movedPathByOldPath.get(layer.layerPath);
      if (exactMovedPath) {
        return findMovedLayerInTree(
          nextLayers,
          layer,
          exactMovedPath,
          containerInstancePath
        );
      }

      const identityMatches = movedChanges.filter(
        (change) =>
          layer.uid !== undefined &&
          change.layerUid === layer.uid &&
          change.layerName === layer.nm
      );
      const identityPath =
        identityMatches.length === 1 ? identityMatches[0].newPath! : undefined;

      return findMovedLayerInTree(
        nextLayers,
        layer,
        identityPath,
        containerInstancePath
      );
    })
    .filter((layer): layer is TimelineLayerInfo => Boolean(layer))
    .map(buildSelection);

  return selections;
};

// Build mod connection map
const buildConnectionMap = (
  mods: IModModel[]
): Map<string, ModConnection[]> => {
  const map = new Map<string, ModConnection[]>();

  mods
    .filter(
      (mod) =>
        !mod.moddata.isDisabled && !ANIMATE_MOD_TYPES.has(mod.moddata.type)
    )
    .forEach((mod) => {
      const modType = mod.moddata.type;
      const modDef = modsDefinitions[modType];
      const color = modDef?.color || "#888";
      const modId = mod.id;

      collectLayerBindings(mod.moddata, { maxDepth: 10 }).forEach((binding) => {
        buildLayerBindingKeys(binding).forEach((key) => {
          if (!map.has(key)) {
            map.set(key, []);
          }
          const existing = map.get(key)!;
          if (!existing.some((c) => c.modId === modId)) {
            existing.push({ modId, color });
          }
        });
      });
    });

  return map;
};

// Get connections for a layer
const getLayerConnections = (
  layer: TimelineLayerInfo,
  connectionMap: Map<string, ModConnection[]>
): ModConnection[] => {
  const keys = buildLayerBindingKeys({
    layerUid: layer.uid,
    layerName: layer.nm,
    assetId: layer.assetId,
  });

  const connections: ModConnection[] = [];
  const seenModIds = new Set<number>();

  keys.forEach((key) => {
    const keyConnections = connectionMap.get(key);
    if (keyConnections) {
      keyConnections.forEach((conn) => {
        if (!seenModIds.has(conn.modId)) {
          seenModIds.add(conn.modId);
          connections.push(conn);
        }
      });
    }
  });

  return connections;
};

// Result type for findAncestorPrecompKeys
interface SelectionSearchResult {
  ancestorsToExpand: Set<string>;
  matchedNodeKey: string | null;
}

type SelectionMarqueeState = {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  additive: boolean;
};

function createViewportRect(
  containerRect: DOMRect,
  marquee: SelectionMarqueeState
): DOMRect {
  const left = containerRect.left + Math.min(marquee.startX, marquee.currentX);
  const top = containerRect.top + Math.min(marquee.startY, marquee.currentY);
  const right = containerRect.left + Math.max(marquee.startX, marquee.currentX);
  const bottom = containerRect.top + Math.max(marquee.startY, marquee.currentY);

  return new DOMRect(left, top, right - left, bottom - top);
}

function mergeUniqueSelections(
  currentSelections: LayerInspectorSelection[],
  nextSelections: LayerInspectorSelection[],
  additive: boolean
): LayerInspectorSelection[] {
  const mergedSelections = additive ? [...currentSelections] : [];
  const seenKeys = new Set(
    mergedSelections.map((selection) =>
      buildLayerInspectorSelectionKey(selection)
    )
  );

  nextSelections.forEach((selection) => {
    const selectionKey = buildLayerInspectorSelectionKey(selection);
    if (selectionKey && seenKeys.has(selectionKey)) {
      return;
    }

    if (selectionKey) {
      seenKeys.add(selectionKey);
    }
    mergedSelections.push(selection);
  });

  return mergedSelections;
}

const LAYER_SELECTION_KEY_SEPARATOR = "\t";

function getLayerInspectorSelectionKeyString(
  selections: LayerInspectorSelection[] | undefined
): string {
  return (selections ?? [])
    .map((selection) => buildLayerInspectorSelectionKey(selection))
    .filter(Boolean)
    .join(LAYER_SELECTION_KEY_SEPARATOR);
}

function buildLayerInspectorKeySetFromString(keyString: string): Set<string> {
  return new Set(
    keyString ? keyString.split(LAYER_SELECTION_KEY_SEPARATOR) : []
  );
}

/**
 * Find all ancestor precomp nodeKeys for layers matching the selection.
 * Also returns the first matched layer's nodeKey for scrolling purposes.
 */
const findAncestorPrecompKeys = (
  layers: TimelineLayerInfo[],
  selectionKeys: Set<string>,
  parentPrecompKeys: string[] = []
): SelectionSearchResult => {
  const ancestorsToExpand = new Set<string>();
  let matchedNodeKey: string | null = null;

  for (const layer of layers) {
    const isMatch = layerMatchesInspectorKeySet(
      toLayerIdentity(layer),
      selectionKeys
    );

    if (isMatch) {
      // Add all parent precomp keys to the result
      parentPrecompKeys.forEach((key) => ancestorsToExpand.add(key));
      // Record first matched layer for scrolling
      if (!matchedNodeKey) {
        matchedNodeKey = layer.nodeKey;
      }
    }

    // Recurse into children if this is a precomp
    if (layer.children?.length) {
      const childResult = findAncestorPrecompKeys(
        layer.children,
        selectionKeys,
        [...parentPrecompKeys, layer.nodeKey]
      );
      childResult.ancestorsToExpand.forEach((key) =>
        ancestorsToExpand.add(key)
      );
      if (!matchedNodeKey && childResult.matchedNodeKey) {
        matchedNodeKey = childResult.matchedNodeKey;
      }
    }
  }

  return { ancestorsToExpand, matchedNodeKey };
};

// Memoized row wrapper for label list to avoid inline arrow functions per item
const TimelineLabelRow = memo(
  ({
    node,
    connectionMap,
    isPrecompExpanded,
    isSelected,
    isHovered,
    isRowHovered,
    onLayerClick,
    onLayerContextMenu,
    onRowMouseEnter,
    onRowMouseLeave,
    sortable,
    dragDisabledReason,
    dropPosition,
    isDropContainer,
    isDropInside,
  }: {
    node: TimelineTreeNode;
    connectionMap: Map<string, ModConnection[]>;
    isPrecompExpanded: (nodeKey: string) => boolean;
    isSelected: boolean;
    isHovered: boolean;
    isRowHovered: boolean;
    onLayerClick: (layer: TimelineLayerInfo, event: ReactMouseEvent) => void;
    onLayerContextMenu: (
      layer: TimelineLayerInfo,
      event: ReactMouseEvent
    ) => void;
    onRowMouseEnter: (nodeKey: string, layer?: TimelineLayerInfo) => void;
    onRowMouseLeave: () => void;
    sortable: boolean;
    dragDisabledReason?: string;
    dropPosition?: LayerDropLinePosition;
    isDropContainer?: boolean;
    isDropInside?: boolean;
  }) => {
    const renderStartedAt = studioPerfNow();
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
      id: node.nodeKey,
      disabled: !sortable,
    });
    const layer = node.type === "layer" ? node.layer : null;
    const connections = layer ? getLayerConnections(layer, connectionMap) : [];

    const handleClick = useCallback(
      (e: ReactMouseEvent) => {
        if (layer) onLayerClick(layer, e);
      },
      [layer, onLayerClick]
    );
    const handleContextMenu = useCallback(
      (e: ReactMouseEvent) => {
        if (layer) onLayerContextMenu(layer, e);
      },
      [layer, onLayerContextMenu]
    );
    const handleMouseEnter = useCallback(
      () => onRowMouseEnter(node.nodeKey, layer ?? undefined),
      [layer, node.nodeKey, onRowMouseEnter]
    );
    const sortableStyle = sortable && isDragging ? { zIndex: 10 } : undefined;
    const dragHandleProps = sortable
      ? ({
          ...attributes,
          ...listeners,
          "data-drag-enabled": true,
        } as React.HTMLAttributes<HTMLButtonElement>)
      : undefined;

    recordStudioPerf(
      "TimelineLabelRow render",
      studioPerfNow() - renderStartedAt,
      {
        hovered: isHovered,
        rowHovered: isRowHovered,
        selected: isSelected,
        sortable,
        type: node.type,
      }
    );

    return (
      <div
        ref={setNodeRef}
        className={`timeline-sortable-row ${
          isDragging ? "timeline-sortable-row--dragging" : ""
        } ${dropPosition ? `timeline-sortable-row--drop-${dropPosition}` : ""}`}
        style={sortableStyle}
      >
        <TimelineTreeNodeLabel
          node={node}
          isPrecompExpanded={isPrecompExpanded}
          isSelected={isSelected}
          isHovered={isHovered}
          isRowHovered={isRowHovered}
          connections={connections}
          dragHandleProps={dragHandleProps}
          dragDisabledReason={dragDisabledReason}
          isDropContainer={isDropContainer}
          isDropInside={isDropInside}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={onRowMouseLeave}
        />
      </div>
    );
  }
);

// Memoized row wrapper for track list to avoid inline arrow functions per item
const TimelineTrackRow = memo(
  ({
    node,
    compositionStart,
    compositionEnd,
    totalFrames,
    isSelected,
    isHovered,
    isRowHovered,
    onLayerClick,
    onLayerContextMenu,
    onKeyframeClick,
    onRowMouseEnter,
    onRowMouseLeave,
    selectedLayersWithTiming,
    selectedKeyframeIds,
    clipToWorkarea,
  }: {
    node: TimelineTreeNode;
    compositionStart: number;
    compositionEnd: number;
    totalFrames: number;
    isSelected: boolean;
    isHovered: boolean;
    isRowHovered: boolean;
    onLayerClick: (layer: TimelineLayerInfo, event: ReactMouseEvent) => void;
    onLayerContextMenu: (
      layer: TimelineLayerInfo,
      event: ReactMouseEvent
    ) => void;
    onKeyframeClick: (keyframeId: string, event: ReactMouseEvent) => void;
    onRowMouseEnter: (nodeKey: string, layer?: TimelineLayerInfo) => void;
    onRowMouseLeave: () => void;
    selectedLayersWithTiming: SelectedLayerInfo[];
    selectedKeyframeIds: Set<string>;
    clipToWorkarea: boolean;
  }) => {
    const renderStartedAt = studioPerfNow();
    const layer = node.type === "layer" ? node.layer : null;

    const handleClick = useCallback(
      (e: ReactMouseEvent) => {
        if (layer) onLayerClick(layer, e);
      },
      [layer, onLayerClick]
    );
    const handleContextMenu = useCallback(
      (e: ReactMouseEvent) => {
        if (layer) onLayerContextMenu(layer, e);
      },
      [layer, onLayerContextMenu]
    );
    const handleMouseEnter = useCallback(
      () => onRowMouseEnter(node.nodeKey, layer ?? undefined),
      [layer, node.nodeKey, onRowMouseEnter]
    );

    recordStudioPerf(
      "TimelineTrackRow render",
      studioPerfNow() - renderStartedAt,
      {
        hovered: isHovered,
        rowHovered: isRowHovered,
        selected: isSelected,
        type: node.type,
      }
    );

    return (
      <TimelineTreeNodeTrack
        node={node}
        compositionStart={compositionStart}
        compositionEnd={compositionEnd}
        totalFrames={totalFrames}
        isSelected={isSelected}
        isHovered={isHovered}
        isRowHovered={isRowHovered}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onRowMouseLeave}
        selectedLayersWithTiming={selectedLayersWithTiming}
        selectedKeyframeIds={selectedKeyframeIds}
        onKeyframeClick={onKeyframeClick}
        clipToWorkarea={clipToWorkarea}
      />
    );
  }
);

export const TimelinePanel = observer(() => {
  const renderStartedAt = studioPerfNow();
  const {
    currentTab,
    platformStore,
    playerStore,
    timelineStore,
    modsStore,
  } = useMst();
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();
  const {
    player,
    layerInspectorAvailable,
    layerInspectorEnabled,
    layerInspectorMode,
    layerInspectorSelections,
    layerInspectorHoveredLayers,
    jsonFile,
    showLayerInspectorContextMenu,
    moveLayerTargets,
  } = playerStore;

  const { panelRef, containerRef } = useTimelineZoom();
  const tracksRef = useRef<HTMLDivElement>(null);
  const tracksAreaRef = useRef<HTMLDivElement>(null);
  const labelsListRef = useRef<HTMLDivElement>(null);
  const hasInitialFit = useRef(false);
  const suppressNextLayerClickRef = useRef(false);
  const seekWarningLoggedRef = useRef(false);
  const currentFrameRef = useRef(untracked(() => playerStore.currentFrame));
  const timelineExpansionDefaultSignatureRef = useRef<string | null>(null);
  const {
    layers,
    compositionStart,
    frameRate,
    totalFrames,
    contentMinFrame,
    contentMaxFrame,
  } = useTimelineLayers();

  useEffect(() => {
    const dispose = reaction(
      () => playerStore.currentFrame,
      (frame) => {
        currentFrameRef.current = frame;
      },
      { fireImmediately: true }
    );

    return dispose;
  }, [playerStore]);

  const timelineExpansionSignature = useMemo(
    () =>
      [
        platformStore.selectedProjectId ?? "project",
        platformStore.selectedVideoPartName ?? "scene",
        currentTab,
      ].join(":"),
    [
      currentTab,
      platformStore.selectedProjectId,
      platformStore.selectedVideoPartName,
    ]
  );
  const defaultExpandedPrecompKeys = useMemo(
    () => getDefaultExpandedPrecompKeys(layers),
    [layers]
  );
  const isTimelineExpansionDefaultPending =
    layers.length > 0 &&
    timelineExpansionDefaultSignatureRef.current !== timelineExpansionSignature;
  const expandedPrecompsSignature = timelineStore.expandedPrecomps.join(",");

  // Flatten layers inside the observer component so MobX can track expandedPrecomps
  const expandedKeys = useMemo(
    () =>
      new Set(
        isTimelineExpansionDefaultPending
          ? defaultExpandedPrecompKeys
          : timelineStore.expandedPrecomps.slice()
      ),
    // Track by join to avoid Set reference equality issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      defaultExpandedPrecompKeys,
      expandedPrecompsSignature,
      isTimelineExpansionDefaultPending,
    ]
  );
  const allFlatLayers = useMemo(() => flattenAllLayers(layers), [layers]);

  useEffect(() => {
    if (!isTimelineExpansionDefaultPending) {
      return;
    }

    timelineStore.setExpandedPrecomps(defaultExpandedPrecompKeys);
    timelineExpansionDefaultSignatureRef.current = timelineExpansionSignature;
    recordStudioPerf("TimelinePanel default expansion", 0, {
      defaultExpandedPrecomps: defaultExpandedPrecompKeys.length,
      recursiveLayers: allFlatLayers.length,
      signature: timelineExpansionSignature,
    });
  }, [
    allFlatLayers.length,
    defaultExpandedPrecompKeys,
    isTimelineExpansionDefaultPending,
    timelineExpansionSignature,
    timelineStore,
  ]);

  const [showAnimatedOnly, setShowAnimatedOnly] = useState(false);

  const keyframeScanLayers = useMemo(
    () =>
      showAnimatedOnly ? allFlatLayers : flattenLayers(layers, expandedKeys),
    [allFlatLayers, expandedKeys, layers, showAnimatedOnly]
  );

  // Compute keyframe data for visible layers, or all layers when animated-only
  // mode needs hidden descendants to decide which parents should be shown.
  const layerKeyframeData = useLayerKeyframeData(keyframeScanLayers, jsonFile);

  const compositionEnd = compositionStart + totalFrames;

  const compositionMarkers = useMemo(() => {
    if (!jsonFile) {
      return [];
    }

    try {
      return motionAPI.inspect.getMarkers(jsonFile as any).map((marker) => ({
        index: marker.index,
        cm: marker.name,
        tm: marker.time,
        dr: marker.duration,
      }));
    } catch (error) {
      console.warn("Timeline marker inspection failed", getErrorLogPayload(error));
      return [];
    }
  }, [jsonFile]);

  const animatedTreeState = useMemo(() => {
    const visibleKeys = new Set<string>();
    const expandedAnimatedKeys = new Set<string>();

    if (!showAnimatedOnly) {
      return {
        visibleKeys,
        expandedAnimatedKeys,
      };
    }

    collectAnimatedTreeState(
      layers,
      layerKeyframeData,
      visibleKeys,
      expandedAnimatedKeys
    );
    return {
      visibleKeys,
      expandedAnimatedKeys,
    };
  }, [showAnimatedOnly, layers, layerKeyframeData]);

  const effectiveExpandedKeys = useMemo(() => {
    if (!showAnimatedOnly) {
      return expandedKeys;
    }

    const mergedExpandedKeys = new Set(expandedKeys);
    animatedTreeState.expandedAnimatedKeys.forEach((nodeKey) => {
      mergedExpandedKeys.add(nodeKey);
    });
    return mergedExpandedKeys;
  }, [showAnimatedOnly, expandedKeys, animatedTreeState]);

  const isPrecompExpanded = useCallback(
    (nodeKey: string) => effectiveExpandedKeys.has(nodeKey),
    [effectiveExpandedKeys]
  );

  // Create visibility callback for keyframes (needs to react to MobX state changes)
  const isLayerKeyframesExpanded = useCallback(
    (nodeKey: string) => timelineStore.isLayerKeyframesExpanded(nodeKey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      timelineStore.keyframesCollapsed,
      timelineStore.keyframeExpandedLayers.length,
    ]
  );

  // Flatten into unified tree nodes (layers and properties)
  const treeNodes = useMemo(
    () =>
      flattenTimelineTree(
        layers,
        effectiveExpandedKeys,
        layerKeyframeData,
        isLayerKeyframesExpanded
      ),
    [layers, effectiveExpandedKeys, layerKeyframeData, isLayerKeyframesExpanded]
  );

  // Filter tree nodes using the active visibility toggles
  const filteredTreeNodes = useMemo(() => {
    if (!timelineStore.clipToWorkarea && !showAnimatedOnly) {
      return treeNodes;
    }

    return treeNodes.filter((node) => {
      const layer = node.type === "property" ? node.parentLayer : node.layer;

      if (
        showAnimatedOnly &&
        !animatedTreeState.visibleKeys.has(layer.nodeKey)
      ) {
        return false;
      }

      if (!timelineStore.clipToWorkarea) {
        return true;
      }

      if (node.type === "property") {
        // For property nodes, check parent layer
        return layerOverlapsWorkarea(layer, compositionStart, compositionEnd);
      }
      // For layer nodes
      return layerOverlapsWorkarea(layer, compositionStart, compositionEnd);
    });
  }, [
    treeNodes,
    timelineStore.clipToWorkarea,
    compositionStart,
    compositionEnd,
    showAnimatedOnly,
    animatedTreeState,
  ]);

  // Global mute state
  const [muteState, setMuteState] = useState(
    MuterSingleton.instance.isMuting()
  );

  // State for auto-scroll to selected layer (set when expanding comps)
  const [pendingScrollTarget, setPendingScrollTarget] = useState<string | null>(
    null
  );

  // State for unified row hover between left and right panels
  const [hoveredNodeKey, setHoveredNodeKey] = useState<string | null>(null);
  const [selectedKeyframeIds, setSelectedKeyframeIds] = useState<string[]>([]);
  const [
    keyframeMarquee,
    setKeyframeMarquee,
  ] = useState<SelectionMarqueeState | null>(null);
  const [
    layerMarquee,
    setLayerMarquee,
  ] = useState<SelectionMarqueeState | null>(null);
  const [activeLayerDragId, setActiveLayerDragId] = useState<string | null>(
    null
  );
  const [
    layerDropIntent,
    setLayerDropIntent,
  ] = useState<LayerDropIntent | null>(null);

  const selectedKeyframeIdSet = useMemo(() => new Set(selectedKeyframeIds), [
    selectedKeyframeIds,
  ]);
  const clearLayerSelectionsForKeyframes = useCallback(() => {
    if (layerInspectorSelections.length) {
      setLayerInspectorSelections([]);
    }
  }, [layerInspectorSelections.length]);
  const selectedKeyframeDeletionTargets = useMemo(
    () =>
      collectSelectedKeyframeDeletionTargets(
        allFlatLayers,
        layerKeyframeData,
        selectedKeyframeIdSet
      ),
    [allFlatLayers, layerKeyframeData, selectedKeyframeIdSet]
  );
  const deleteSelectedKeyframes = useCallback(() => {
    if (!selectedKeyframeIds.length) {
      return;
    }

    if (selectedKeyframeDeletionTargets.length) {
      applyAnimationJsonEdit((workingAnimation) =>
        applyKeyframeDeletionTargets(
          workingAnimation,
          selectedKeyframeDeletionTargets
        )
      );
    }

    setSelectedKeyframeIds([]);
  }, [
    applyAnimationJsonEdit,
    selectedKeyframeDeletionTargets,
    selectedKeyframeIds.length,
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        isKeyboardShortcutSuppressedTarget(e.target) ||
        !isTimelineDeleteKey(e) ||
        !selectedKeyframeIds.length
      ) {
        return;
      }

      if (
        currentTab !== "layers" ||
        !layerInspectorEnabled ||
        layerInspectorMode !== "design"
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (e.repeat) {
        return;
      }

      deleteSelectedKeyframes();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [
    currentTab,
    deleteSelectedKeyframes,
    layerInspectorEnabled,
    layerInspectorMode,
    selectedKeyframeIds.length,
  ]);
  const layerDragPointerRef = useRef<{ x: number; y: number } | null>(null);
  const layerDropIntentRef = useRef<LayerDropIntent | null>(null);
  const layerSortSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );
  const layerDropCollisionDetection = useCallback<CollisionDetection>(
    (args) => {
      layerDragPointerRef.current = args.pointerCoordinates;
      const pointerCollisions = pointerWithin(args);
      return pointerCollisions.length ? pointerCollisions : closestCenter(args);
    },
    []
  );

  // Get root asset name for inspector
  const rootAssetName = (jsonFile as { nm?: string } | undefined)?.nm ?? "root";

  // Build inspector selection from layer
  const buildInspectorSelection = useCallback(
    (layer: TimelineLayerInfo): LayerInspectorSelection => {
      const classNameAsset = layer.assetName ?? layer.assetId ?? rootAssetName;
      const className =
        typeof layer.uid === "number"
          ? buildLayerUidClassName(layer.uid, classNameAsset)
          : layer.nm
          ? buildLayerNameClassName(layer.nm, classNameAsset)
          : undefined;

      return {
        path: layer.layerPath,
        instancePath: layer.instancePath,
        className,
        assetName: layer.assetName ?? layer.assetId,
        layerName: layer.nm,
        layerUid: layer.uid,
        layerType: layer.ty,
        refId: layer.refId,
      };
    },
    [rootAssetName]
  );

  const layerSelectionsByNodeKey = useMemo(() => {
    const selections = new Map<string, LayerInspectorSelection>();

    filteredTreeNodes.forEach((node) => {
      if (node.type !== "layer") return;
      selections.set(node.nodeKey, buildInspectorSelection(node.layer));
    });

    return selections;
  }, [buildInspectorSelection, filteredTreeNodes]);

  const layerInspectorSelectionKey = useMemo(
    () => getLayerInspectorSelectionKeyString(layerInspectorSelections),
    [layerInspectorSelections]
  );

  const inspectorSelectedKeys = useMemo(
    () => buildLayerInspectorKeySetFromString(layerInspectorSelectionKey),
    [layerInspectorSelectionKey]
  );

  const inspectorHoveredKeys = useMemo(
    () => buildLayerInspectorKeySet(layerInspectorHoveredLayers ?? []),
    [layerInspectorHoveredLayers]
  );
  const emptyInspectorHoveredKeys = useMemo(() => new Set<string>(), []);
  const visibleHoveredNodeKey = activeLayerDragId ? null : hoveredNodeKey;
  const visibleInspectorHoveredKeys = activeLayerDragId
    ? emptyInspectorHoveredKeys
    : inspectorHoveredKeys;

  const layerByNodeKey = useMemo(() => {
    const map = new Map<string, TimelineLayerInfo>();
    allFlatLayers.forEach(({ layer }) => {
      map.set(layer.nodeKey, layer);
    });
    return map;
  }, [allFlatLayers]);

  const parentLayerByNodeKey = useMemo(() => {
    const map = new Map<string, TimelineLayerInfo>();

    const visit = (
      layerList: TimelineLayerInfo[],
      parentLayer?: TimelineLayerInfo
    ) => {
      layerList.forEach((layer) => {
        if (parentLayer) {
          map.set(layer.nodeKey, parentLayer);
        }
        if (layer.children?.length) {
          visit(layer.children, layer);
        }
      });
    };

    visit(layers);
    return map;
  }, [layers]);

  const sortableLayerIds = useMemo(
    () =>
      filteredTreeNodes
        .filter((node) => node.type === "layer")
        .map((node) => node.nodeKey),
    [filteredTreeNodes]
  );
  const sortableLayerIdSet = useMemo(() => new Set(sortableLayerIds), [
    sortableLayerIds,
  ]);

  const setNextLayerDropIntent = useCallback((next: LayerDropIntent | null) => {
    layerDropIntentRef.current = next;
    setLayerDropIntent((previous) =>
      getLayerDropIntentKey(previous) === getLayerDropIntentKey(next)
        ? previous
        : next
    );
  }, []);

  const getDraggedLayersForActive = useCallback(
    (activeLayer: TimelineLayerInfo): TimelineLayerInfo[] => {
      const selectedLayers = allFlatLayers
        .map(({ layer }) => layer)
        .filter((layer) =>
          layerMatchesInspectorKeySet(
            toLayerIdentity(layer),
            inspectorSelectedKeys
          )
        );
      const activeIsSelected = selectedLayers.some(
        (layer) => layer.nodeKey === activeLayer.nodeKey
      );

      return activeIsSelected && selectedLayers.length
        ? removeLayersCoveredByDraggedAncestor(selectedLayers)
        : [activeLayer];
    },
    [allFlatLayers, inspectorSelectedKeys]
  );

  const buildLayerDropIntent = useCallback(
    (
      event: DragMoveEvent | DragOverEvent | DragEndEvent
    ): LayerDropIntent | null => {
      const activeId = String(event.active.id);
      const overId = event.over ? String(event.over.id) : null;
      if (!overId) {
        return null;
      }

      const activeLayer = layerByNodeKey.get(activeId);
      const overLayer = layerByNodeKey.get(overId);
      if (!activeLayer || !overLayer) {
        return null;
      }

      const draggedLayers = getDraggedLayersForActive(activeLayer);
      if (isLayerCoveredByDraggedClosure(overLayer, draggedLayers)) {
        return null;
      }

      const overTarget = buildMotionLayerTarget(overLayer);
      const pointerY =
        layerDragPointerRef.current?.y ??
        event.active.rect.current.translated?.top ??
        event.over?.rect.top ??
        0;
      const overRect = event.over?.rect;
      const dropZone = getLayerDropZone(pointerY, overRect);

      if (dropZone === "inside" && isPrecompContainerLayer(overLayer)) {
        return {
          kind: "inside",
          activeId,
          overId,
          containerNodeKey: overLayer.nodeKey,
          moveOptions: {
            container: { assetId: overLayer.refId },
            order: { toIndex: 0 },
            reparent: draggedLayers.some(hasParentReference)
              ? { target: null, preserveWorldTransform: false }
              : undefined,
          },
        };
      }

      const placement: LayerDropLinePosition =
        dropZone === "inside" ? "after" : dropZone;
      const parentLayer = parentLayerByNodeKey.get(overLayer.nodeKey);
      const shouldDetachParent = shouldDetachExternalParent(
        draggedLayers,
        overLayer
      );

      return {
        kind: "insert",
        activeId,
        overId,
        placement,
        containerNodeKey: parentLayer?.nodeKey,
        moveOptions: {
          container: { target: overTarget },
          order:
            placement === "before"
              ? { before: overTarget }
              : { after: overTarget },
          reparent: shouldDetachParent
            ? { target: null, preserveWorldTransform: false }
            : undefined,
        },
      };
    },
    [getDraggedLayersForActive, layerByNodeKey, parentLayerByNodeKey]
  );

  const updateLayerDropIntent = useCallback(
    (event: DragMoveEvent | DragOverEvent) => {
      setNextLayerDropIntent(buildLayerDropIntent(event));
    },
    [buildLayerDropIntent, setNextLayerDropIntent]
  );

  const clearLayerDropState = useCallback(() => {
    layerDragPointerRef.current = null;
    setActiveLayerDragId(null);
    setNextLayerDropIntent(null);
  }, [setNextLayerDropIntent]);

  const handleLayerSortDragStart = useCallback((event: DragStartEvent) => {
    setActiveLayerDragId(String(event.active.id));
    setHoveredNodeKey(null);
    clearLayerInspectorHover();
  }, []);

  const handleLayerSortDragCancel = useCallback(() => {
    clearLayerDropState();
  }, [clearLayerDropState]);

  const handleLayerSortDragEnd = useCallback(
    (event: DragEndEvent) => {
      const intent = layerDropIntentRef.current ?? buildLayerDropIntent(event);
      const activeLayer = layerByNodeKey.get(String(event.active.id));
      clearLayerDropState();

      if (!intent || !activeLayer) {
        return;
      }

      const draggedLayers = getDraggedLayersForActive(activeLayer);
      const sourceTargets = draggedLayers.map(buildMotionLayerTarget);
      const destinationContainerInstancePath = intent.containerNodeKey
        ? layerByNodeKey.get(intent.containerNodeKey)?.instancePath
        : undefined;
      if (!sourceTargets.length) {
        return;
      }

      void (async () => {
        try {
          const result = await moveLayerTargets(
            sourceTargets,
            intent.moveOptions
          );
          if (!result || result.blockedReason || !result.changes.length) {
            return;
          }

          if (intent.kind === "inside") {
            timelineStore.setPrecompExpanded(intent.containerNodeKey, true);
          }

          const refreshedSelections = buildMovedLayerSelections(
            result,
            draggedLayers,
            buildInspectorSelection,
            destinationContainerInstancePath
          );
          setLayerInspectorSelections(refreshedSelections);
        } catch (error) {
          console.warn("[Timeline layer move] failed to move layers", {
            activeLayer: activeLayer.nm,
            intent,
            error: getErrorLogPayload(error),
          });
        }
      })();
    },
    [
      buildInspectorSelection,
      buildLayerDropIntent,
      clearLayerDropState,
      getDraggedLayersForActive,
      layerByNodeKey,
      moveLayerTargets,
      timelineStore,
    ]
  );

  // Handlers for unified row hover
  const handleRowMouseEnter = useCallback(
    (nodeKey: string, layer?: TimelineLayerInfo) => {
      if (activeLayerDragId) {
        return;
      }

      setHoveredNodeKey(nodeKey);

      if (!layerInspectorAvailable || !layerInspectorEnabled || !layer) {
        return;
      }

      setLayerInspectorHover(buildInspectorSelection(layer));
    },
    [
      activeLayerDragId,
      buildInspectorSelection,
      layerInspectorAvailable,
      layerInspectorEnabled,
    ]
  );

  const handleRowMouseLeave = useCallback(() => {
    setHoveredNodeKey(null);
    clearLayerInspectorHover();
  }, []);

  const handleTracksMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.button !== 0 || !tracksRef.current) return;

      const target = event.target as HTMLElement;
      if (
        target.closest(
          ".timeline-keyframe-marker, .timeline-keyframe-row__segment, .timeline-track-bar, .timeline-track-bar__edge-handle, .timeline-scene-trim-bar__handle, .timeline-playhead__head"
        )
      ) {
        return;
      }

      const rect = tracksRef.current.getBoundingClientRect();
      setKeyframeMarquee({
        startX: event.clientX - rect.left,
        startY: event.clientY - rect.top,
        currentX: event.clientX - rect.left,
        currentY: event.clientY - rect.top,
        additive: event.metaKey || event.ctrlKey,
      });
      event.preventDefault();
    },
    []
  );

  const handleLabelsMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.button !== 0 || !labelsListRef.current) return;

      const target = event.target as HTMLElement;
      if (
        target.closest(
          ".timeline-tree-node__toggle, .timeline-tree-node__keyframe-toggle, .timeline-tree-node__add-action, .timeline-tree-node__row-action, .timeline-tree-node__drag-handle, button, input"
        )
      ) {
        return;
      }

      const rect = labelsListRef.current.getBoundingClientRect();
      setLayerMarquee({
        startX: event.clientX - rect.left,
        startY: event.clientY - rect.top,
        currentX: event.clientX - rect.left,
        currentY: event.clientY - rect.top,
        additive: event.metaKey || event.ctrlKey,
      });
      event.preventDefault();
    },
    []
  );

  useEffect(() => {
    if (!keyframeMarquee || !tracksRef.current) return;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = tracksRef.current?.getBoundingClientRect();
      if (!rect) return;

      setKeyframeMarquee((previous) =>
        previous
          ? {
              ...previous,
              currentX: event.clientX - rect.left,
              currentY: event.clientY - rect.top,
            }
          : previous
      );
    };

    const handleMouseUp = () => {
      const container = tracksRef.current;
      if (!container) {
        setKeyframeMarquee(null);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const selectionRect = createViewportRect(containerRect, keyframeMarquee);
      const draggedFarEnough =
        Math.abs(keyframeMarquee.currentX - keyframeMarquee.startX) >=
          KEYFRAME_SELECTION_MIN_DRAG_PX ||
        Math.abs(keyframeMarquee.currentY - keyframeMarquee.startY) >=
          KEYFRAME_SELECTION_MIN_DRAG_PX;

      const marqueeSelection = draggedFarEnough
        ? getTimelineMarqueeSelection({
            container,
            selectionRect,
            layerSelectionsByNodeKey,
          })
        : null;
      const intersectingKeyframeIds =
        marqueeSelection?.type === "keyframes"
          ? marqueeSelection.keyframeIds
          : [];
      const intersectingLayerSelections =
        marqueeSelection?.type === "layers"
          ? marqueeSelection.layerSelections
          : [];
      const shouldSelectLayers = marqueeSelection?.type === "layers";

      if (intersectingKeyframeIds.length) {
        clearLayerSelectionsForKeyframes();
      }

      setSelectedKeyframeIds((previous) => {
        if (!draggedFarEnough) {
          return keyframeMarquee.additive ? previous : [];
        }

        if (shouldSelectLayers) {
          return [];
        }

        if (!keyframeMarquee.additive) {
          return intersectingKeyframeIds;
        }

        const next = new Set(previous);
        intersectingKeyframeIds.forEach((id) => next.add(id));
        return Array.from(next);
      });

      if (shouldSelectLayers && layerInspectorAvailable) {
        if (
          intersectingLayerSelections.length &&
          !layerInspectorEnabled
        ) {
          setLayerInspectorEnabled(true);
        }

        const currentSelections = layerInspectorSelections ?? [];
        const nextSelections = mergeUniqueSelections(
          currentSelections,
          intersectingLayerSelections,
          keyframeMarquee.additive
        );

        setLayerInspectorSelections(nextSelections);
      }

      setKeyframeMarquee(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    keyframeMarquee,
    clearLayerSelectionsForKeyframes,
    layerInspectorAvailable,
    layerInspectorEnabled,
    layerInspectorSelections,
    layerSelectionsByNodeKey,
  ]);

  useEffect(() => {
    if (!layerMarquee || !labelsListRef.current) return;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = labelsListRef.current?.getBoundingClientRect();
      if (!rect) return;

      setLayerMarquee((previous) =>
        previous
          ? {
              ...previous,
              currentX: event.clientX - rect.left,
              currentY: event.clientY - rect.top,
            }
          : previous
      );
    };

    const handleMouseUp = () => {
      const container = labelsListRef.current;
      if (!container) {
        setLayerMarquee(null);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const selectionRect = createViewportRect(containerRect, layerMarquee);
      const draggedFarEnough =
        Math.abs(layerMarquee.currentX - layerMarquee.startX) >=
          KEYFRAME_SELECTION_MIN_DRAG_PX ||
        Math.abs(layerMarquee.currentY - layerMarquee.startY) >=
          KEYFRAME_SELECTION_MIN_DRAG_PX;

      const intersectingSelections = draggedFarEnough
        ? Array.from(
            container.querySelectorAll<HTMLElement>(
              ".timeline-tree-node--layer[data-node-key]"
            )
          )
            .filter((element) =>
              rectsIntersect(selectionRect, element.getBoundingClientRect())
            )
            .map((element) => {
              const nodeKey = element.dataset.nodeKey;
              return nodeKey
                ? layerSelectionsByNodeKey.get(nodeKey)
                : undefined;
            })
            .filter(
              (selection): selection is LayerInspectorSelection =>
                selection !== undefined
            )
        : [];

      if (draggedFarEnough) {
        suppressNextLayerClickRef.current = true;
        window.setTimeout(() => {
          suppressNextLayerClickRef.current = false;
        }, 0);
        setSelectedKeyframeIds([]);
      }

      if (layerInspectorAvailable) {
        if (
          draggedFarEnough &&
          intersectingSelections.length &&
          !layerInspectorEnabled
        ) {
          setLayerInspectorEnabled(true);
        }

        const currentSelections = layerInspectorSelections ?? [];
        const nextSelections = !draggedFarEnough
          ? layerMarquee.additive
            ? currentSelections
            : []
          : mergeUniqueSelections(
              currentSelections,
              intersectingSelections,
              layerMarquee.additive
            );

        setLayerInspectorSelections(nextSelections);
      }

      setLayerMarquee(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    layerInspectorAvailable,
    layerInspectorEnabled,
    layerMarquee,
    layerInspectorSelections,
    layerSelectionsByNodeKey,
  ]);

  const handleToggleAnimatedOnly = useCallback(() => {
    setShowAnimatedOnly((current) => {
      const next = !current;

      if (next && timelineStore.keyframesCollapsed) {
        timelineStore.toggleGlobalKeyframesCollapsed();
      }

      return next;
    });
  }, [timelineStore]);

  const handleKeyframeClick = useCallback(
    (keyframeId: string, event: ReactMouseEvent) => {
      const additive = event.shiftKey || event.metaKey || event.ctrlKey;
      clearLayerSelectionsForKeyframes();

      setSelectedKeyframeIds((previous) => {
        if (!additive) {
          return [keyframeId];
        }

        const next = new Set(previous);
        if (event.metaKey || event.ctrlKey) {
          if (next.has(keyframeId)) {
            next.delete(keyframeId);
          } else {
            next.add(keyframeId);
          }
          return Array.from(next);
        }

        next.add(keyframeId);
        return Array.from(next);
      });
    },
    [clearLayerSelectionsForKeyframes]
  );

  // Measure viewport width and update store context
  const workAreaFrames = totalFrames;

  useEffect(() => {
    // Use tracksAreaRef for width measurement (not containerRef which includes labels)
    const tracksArea = tracksAreaRef.current;
    if (!tracksArea) return;

    const updateContext = (width: number) => {
      timelineStore.setWorkAreaContext(workAreaFrames, width);
      // Fit to view on initial load (100% zoom)
      if (!hasInitialFit.current && width > 0 && workAreaFrames > 0) {
        timelineStore.fitToView();
        hasInitialFit.current = true;
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateContext(entry.contentRect.width);
      }
    });

    observer.observe(tracksArea);
    // Initial update
    updateContext(tracksArea.clientWidth);
    return () => observer.disconnect();
  }, [workAreaFrames, timelineStore]);

  // Ensure horizontal scroll is always 0 (scroll is handled virtually via scrollOffset)
  // Only run once on mount — scroll is managed virtually via scrollOffset, so the
  // physical scrollLeft should never drift. If a future change causes it to drift,
  // add the relevant trigger to the dependency array instead of removing it.
  useEffect(() => {
    if (tracksRef.current && tracksRef.current.scrollLeft !== 0) {
      tracksRef.current.scrollLeft = 0;
    }
  }, []);

  // Update content extent for zoom limits
  useEffect(() => {
    timelineStore.setContentExtent(contentMinFrame, contentMaxFrame);
  }, [contentMinFrame, contentMaxFrame, timelineStore]);

  // Auto-expand collapsed compositions when a layer inside them is selected
  useEffect(() => {
    if (inspectorSelectedKeys.size === 0 || !layers?.length) return;

    const { ancestorsToExpand, matchedNodeKey } = findAncestorPrecompKeys(
      layers,
      inspectorSelectedKeys
    );

    // Expand each ancestor that isn't already expanded
    ancestorsToExpand.forEach((nodeKey) => {
      if (!timelineStore.isPrecompExpanded(nodeKey)) {
        timelineStore.setPrecompExpanded(nodeKey, true);
      }
    });

    // Set pending scroll target - will scroll after expansion re-renders
    if (matchedNodeKey) {
      setPendingScrollTarget(matchedNodeKey);
    }
  }, [inspectorSelectedKeys, layers, timelineStore]);

  // Scroll to the selected layer after expansion completes
  useEffect(() => {
    if (!pendingScrollTarget || !treeNodes.length) return;

    // Find the index of the matched layer in the flattened tree
    const nodeIndex = treeNodes.findIndex(
      (node) => node.nodeKey === pendingScrollTarget
    );

    if (nodeIndex >= 0 && containerRef.current) {
      const stickyHeaderHeight = 24;
      // Calculate scroll position
      const rowTop = stickyHeaderHeight + nodeIndex * TREE_ROW_HEIGHT;
      const containerHeight = containerRef.current.clientHeight;
      const viewportHeight = containerHeight - stickyHeaderHeight;

      // Only scroll if the layer is not already visible
      const currentScrollTop = containerRef.current.scrollTop;
      const isVisible =
        rowTop >= currentScrollTop + stickyHeaderHeight &&
        rowTop < currentScrollTop + containerHeight - TREE_ROW_HEIGHT;

      if (!isVisible && viewportHeight > 0) {
        // Center the layer in the viewport
        const targetScroll = Math.max(
          0,
          rowTop - stickyHeaderHeight - viewportHeight / 2 + TREE_ROW_HEIGHT / 2
        );
        containerRef.current.scrollTop = targetScroll;
      }
    }

    // Clear the pending target
    setPendingScrollTarget(null);
  }, [containerRef, pendingScrollTarget, treeNodes]);

  // Build connection map
  const mods = useMemo(() => modsStore.mods?.slice() ?? [], [modsStore.mods]);

  const connectionMap = useMemo(() => {
    return buildConnectionMap(mods as IModModel[]);
  }, [mods]);

  // Build selectedLayersWithTiming for multi-select layer dragging
  const selectedLayersWithTiming: SelectedLayerInfo[] = useMemo(() => {
    if (inspectorSelectedKeys.size === 0) return [];

    // Find all layer nodes that match the selection
    const selectedLayers: SelectedLayerInfo[] = [];
    for (const node of treeNodes) {
      if (node.type !== "layer") continue;
      const layer = node.layer;
      if (
        layerMatchesInspectorKeySet(
          toLayerIdentity(layer),
          inspectorSelectedKeys
        )
      ) {
        selectedLayers.push({
          layer,
          resolvedTiming: getResolvedLayerTiming(layer, mods),
        });
      }
    }
    return selectedLayers;
  }, [treeNodes, inspectorSelectedKeys, mods]);

  const getBestFrameForLayer = useCallback(
    (layer: TimelineLayerInfo): number => {
      const resolvedTiming = getResolvedLayerTiming(layer, mods);
      const hasTimingOverride =
        resolvedTiming.ip !== layer.ip || resolvedTiming.op !== layer.op;
      let apiTiming: { ip: number; op: number } | undefined;

      if (jsonFile) {
        try {
          apiTiming = motionAPI.inspect.getLayerTiming(
            jsonFile as any,
            buildMotionLayerTarget(layer)
          );
        } catch {
          apiTiming = undefined;
        }
      }

      const timelineOffset = layer.timelineOffset ?? 0;
      const useApiTiming = apiTiming && !hasTimingOverride;
      const rawInFrame =
        useApiTiming && Number.isFinite(apiTiming.ip)
          ? apiTiming.ip + timelineOffset
          : resolvedTiming.ip;
      const rawOutFrame =
        useApiTiming && Number.isFinite(apiTiming.op)
          ? apiTiming.op + timelineOffset
          : resolvedTiming.op;
      const maxCompositionFrame = Math.max(
        compositionStart,
        compositionEnd - 1
      );
      const firstFrame = Math.max(compositionStart, Math.floor(rawInFrame));
      const lastFrame = Math.min(
        maxCompositionFrame,
        Math.max(firstFrame, Math.ceil(rawOutFrame) - 1)
      );

      if (firstFrame > maxCompositionFrame) {
        return maxCompositionFrame;
      }

      const frameNearInPoint = firstFrame + 5;
      const frameBeforeOutPoint = Math.ceil(rawOutFrame) - 5;
      const bestFrame = Math.min(frameNearInPoint, frameBeforeOutPoint);

      return Math.min(lastFrame, Math.max(firstFrame, bestFrame));
    },
    [compositionEnd, compositionStart, jsonFile, mods]
  );

  const isLayerVisibleAtFrame = useCallback(
    (layer: TimelineLayerInfo, frame: number): boolean => {
      if (!Number.isFinite(frame)) {
        return true;
      }

      const resolvedTiming = getResolvedLayerTiming(layer, mods);
      return frame >= resolvedTiming.ip && frame < resolvedTiming.op;
    },
    [mods]
  );

  // Handle seek from ruler or layer selection
  const handleSeek = useCallback(
    (frame: number) => {
      const timelinePlayer = player as TimelineSeekPlayer | undefined;
      const seek = getTimelineSeek(timelinePlayer);

      if (!Number.isFinite(frame) || !seek) {
        return;
      }

      const seekFrame = Math.round(frame);
      const seekPromise = seek(seekFrame);
      seekPromise?.catch((error) => {
        if (seekWarningLoggedRef.current) return;

        seekWarningLoggedRef.current = true;
        console.warn("Timeline seek failed", getErrorLogPayload(error));
      });
    },
    [player]
  );

  const handleCreateMarker = useCallback(() => {
    const markerFrame = getBoundedCompositionFrame(
      currentFrameRef.current,
      compositionStart,
      compositionEnd
    );

    applyAnimationJsonEdit(
      (workingAnimation) => {
        const markerCount = motionAPI.inspect.getMarkers(
          workingAnimation as any
        ).length;
        const applied = motionAPI.apply.createMarker(
          workingAnimation as any,
          {
            name: `Marker ${markerCount + 1}`,
            time: markerFrame,
          }
        );

        return applied.changes.length ? applied : false;
      },
      {
        playerUpdate: {
          immediate: true,
          startFrame: markerFrame,
        },
      }
    );
  }, [applyAnimationJsonEdit, compositionEnd, compositionStart]);

  const handleUpdateMarker = useCallback(
    (markerIndex: number, patch: MotionMarkerPatch) => {
      const nextPatch = {
        ...patch,
        ...(patch.time != null
          ? {
              time: getBoundedCompositionFrame(
                patch.time,
                compositionStart,
                compositionEnd
              ),
            }
          : {}),
      };
      const startFrame =
        nextPatch.time ??
        getBoundedCompositionFrame(
          currentFrameRef.current,
          compositionStart,
          compositionEnd
        );

      applyAnimationJsonEdit(
        (workingAnimation) => {
          const applied = motionAPI.apply.updateMarker(
            workingAnimation as any,
            markerIndex,
            nextPatch
          );

          return applied.changes.length ? applied : false;
        },
        {
          playerUpdate: {
            startFrame,
          },
        }
      );
    },
    [applyAnimationJsonEdit, compositionEnd, compositionStart]
  );

  const handleDeleteMarker = useCallback(
    (markerIndex: number) => {
      const startFrame = getBoundedCompositionFrame(
        currentFrameRef.current,
        compositionStart,
        compositionEnd
      );

      applyAnimationJsonEdit(
        (workingAnimation) => {
          const applied = motionAPI.apply.deleteMarker(
            workingAnimation as any,
            markerIndex
          );

          return applied.changes.length ? applied : false;
        },
        {
          playerUpdate: {
            startFrame,
          },
        }
      );
    },
    [applyAnimationJsonEdit, compositionEnd, compositionStart]
  );

  // Handle layer click
  const handleLayerClick = useCallback(
    (layer: TimelineLayerInfo, event: ReactMouseEvent) => {
      if (suppressNextLayerClickRef.current) {
        suppressNextLayerClickRef.current = false;
        return;
      }

      setSelectedKeyframeIds([]);

      if (!layerInspectorAvailable) return;

      if (!layerInspectorEnabled) {
        setLayerInspectorEnabled(true);
      }

      const inspectorSelection = buildInspectorSelection(layer);
      const currentSelections = layerInspectorSelections ?? [];
      const selectionKey = buildLayerInspectorSelectionKey(inspectorSelection);
      const currentKeys = new Set(
        currentSelections.map((entry) => buildLayerInspectorSelectionKey(entry))
      );
      const wasSelected = currentKeys.has(selectionKey);
      const nextSelections = getNextLayerInspectorSelections({
        currentSelections,
        selection: inspectorSelection,
        additive: event.shiftKey || event.metaKey || event.ctrlKey,
      });

      setLayerInspectorSelections(nextSelections);

      const layerIsSelected = nextSelections.some(
        (selection) =>
          buildLayerInspectorSelectionKey(selection) === selectionKey
      );
      const currentFrame = currentFrameRef.current;

      if (
        !wasSelected &&
        layerIsSelected &&
        !isLayerVisibleAtFrame(layer, currentFrame)
      ) {
        handleSeek(getBestFrameForLayer(layer));
      }
    },
    [
      layerInspectorAvailable,
      layerInspectorEnabled,
      layerInspectorSelections,
      buildInspectorSelection,
      getBestFrameForLayer,
      handleSeek,
      isLayerVisibleAtFrame,
    ]
  );

  // Handle layer context menu
  const handleLayerContextMenu = useCallback(
    (layer: TimelineLayerInfo, event: ReactMouseEvent) => {
      setSelectedKeyframeIds([]);

      if (!layerInspectorAvailable) return;
      event.preventDefault();

      if (!layerInspectorEnabled) {
        setLayerInspectorEnabled(true);
      }

      const inspectorSelection = buildInspectorSelection(layer);
      const selectionKey = buildLayerInspectorSelectionKey(inspectorSelection);
      const currentSelections = layerInspectorSelections ?? [];
      const currentKeys = new Set(
        currentSelections.map((entry) => buildLayerInspectorSelectionKey(entry))
      );

      // Keep current selections if right-clicked layer is already selected
      const nextSelections = currentKeys.has(selectionKey)
        ? currentSelections
        : [inspectorSelection];

      if (!currentKeys.has(selectionKey)) {
        setLayerInspectorSelections(nextSelections);
      }

      showLayerInspectorContextMenu(
        {
          clientX: event.clientX,
          clientY: event.clientY,
          pageX: event.pageX,
          pageY: event.pageY,
        },
        nextSelections.length ? nextSelections : [inspectorSelection],
        inspectorSelection
      );
    },
    [
      layerInspectorAvailable,
      layerInspectorEnabled,
      layerInspectorSelections,
      buildInspectorSelection,
      showLayerInspectorContextMenu,
    ]
  );

  // Handle resize
  const handleResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      const startY = e.clientY;
      const startHeight = timelineStore.panelHeight;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = startY - moveEvent.clientY;
        timelineStore.setPanelHeight(startHeight + delta);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [timelineStore]
  );

  const handleLabelResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;

      event.preventDefault();
      const startX = event.clientX;
      const startWidth = timelineStore.labelWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        timelineStore.setLabelWidth(startWidth + moveEvent.clientX - startX);
      };

      const handleMouseUp = () => {
        document.body.classList.remove("timeline-label-resize-active");
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.body.classList.add("timeline-label-resize-active");
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [timelineStore]
  );

  const panelClass = `timeline-panel ${
    timelineStore.isCollapsed ? "timeline-panel--collapsed" : ""
  }`;
  const panelStyle = {
    height: timelineStore.isCollapsed ? 32 : timelineStore.panelHeight,
    "--timeline-label-width": `${timelineStore.labelWidth}px`,
  } as CSSProperties;

  recordStudioPerf("TimelinePanel render", studioPerfNow() - renderStartedAt, {
    currentFrame: Math.round(currentFrameRef.current),
    flatLayers: allFlatLayers.length,
    hoveredLayers: layerInspectorHoveredLayers.length,
    isCollapsed: timelineStore.isCollapsed,
    keyframeScanLayers: keyframeScanLayers.length,
    selectedLayers: layerInspectorSelections.length,
    visibleNodes: filteredTreeNodes.length,
  });

  return (
    <div
      ref={panelRef}
      className={panelClass}
      data-layer-inspector-keep-selection
      style={panelStyle}
    >
      {/* Resize handle */}
      {!timelineStore.isCollapsed && (
        <div
          className="timeline-panel__resize-handle"
          onMouseDown={handleResizeStart}
        />
      )}

      {/* Header */}
      <div className="timeline-panel__header">
        <button
          className={`timeline-panel__collapse-btn ${
            timelineStore.isCollapsed
              ? "timeline-panel__collapse-btn--collapsed"
              : ""
          }`}
          onClick={() => timelineStore.toggleCollapsed()}
          title={
            timelineStore.isCollapsed ? "Expand Timeline" : "Collapse Timeline"
          }
        >
          {timelineStore.isCollapsed ? (
            <CaretRightOutlined />
          ) : (
            <CaretDownOutlined />
          )}
        </button>
        <span className="timeline-panel__title">Timeline</span>
        {!timelineStore.isCollapsed && (
          <div className="timeline-panel__header-controls">
            <Tooltip title="Add marker at playhead" placement="top">
              <button
                type="button"
                className="timeline-icon-btn"
                onClick={handleCreateMarker}
                aria-label="Add marker at playhead"
              >
                <FlagOutlined />
              </button>
            </Tooltip>
            <FrameIndicator />
            <Tooltip
              title={muteState ? "Unmute all sounds" : "Mute all sounds"}
              placement="top"
            >
              <button
                className="timeline-icon-btn"
                onClick={(e) => {
                  MuterSingleton.instance.toggleMute();
                  setMuteState(MuterSingleton.instance.isMuting());
                  e.currentTarget.blur();
                }}
              >
                {muteState ? <SoundOffIcon /> : <SoundOnIcon />}
              </button>
            </Tooltip>
            <Tooltip
              title={
                timelineStore.keyframesCollapsed
                  ? "Show keyframes"
                  : "Hide keyframes"
              }
              placement="top"
            >
              <button
                className={`timeline-icon-btn ${
                  !timelineStore.keyframesCollapsed
                    ? "timeline-icon-btn--active-gold"
                    : ""
                }`}
                onClick={() => timelineStore.toggleGlobalKeyframesCollapsed()}
              >
                {timelineStore.keyframesCollapsed ? (
                  <KeyframeIcon />
                ) : (
                  <KeyframeFilledIcon />
                )}
              </button>
            </Tooltip>
            <Tooltip
              title={
                timelineStore.clipToWorkarea
                  ? "Show all layers"
                  : "Clip to workarea"
              }
              placement="top"
            >
              <button
                className={`timeline-icon-btn ${
                  timelineStore.clipToWorkarea
                    ? "timeline-icon-btn--active-blue"
                    : ""
                }`}
                onClick={() => timelineStore.toggleClipToWorkarea()}
              >
                {timelineStore.clipToWorkarea ? (
                  <WorkareaActiveIcon />
                ) : (
                  <WorkareaIcon />
                )}
              </button>
            </Tooltip>
            <Tooltip
              title={
                showAnimatedOnly
                  ? "Show all layers"
                  : "Show only animated layers"
              }
              placement="top"
            >
              <button
                className={`timeline-icon-btn ${
                  showAnimatedOnly ? "timeline-icon-btn--active-gold" : ""
                }`}
                onClick={handleToggleAnimatedOnly}
              >
                <AnimatedLayersIcon />
              </button>
            </Tooltip>
            <TimelineControls />
          </div>
        )}
      </div>

      {!timelineStore.isCollapsed && (
        <div
          className="timeline-label-resize-handle"
          onMouseDown={handleLabelResizeStart}
          title="Resize layer labels"
        />
      )}

      {/* Body */}
      {!timelineStore.isCollapsed && (
        <div className="timeline-panel__body" ref={containerRef}>
          {/* Left side - Layer labels */}
          <div className="timeline-labels">
            <div className="timeline-labels__header">
              <span>Layers</span>
            </div>
            <div
              className={`timeline-labels__list ${
                activeLayerDragId ? "timeline-labels__list--dragging" : ""
              }`}
              ref={labelsListRef}
              onMouseDown={handleLabelsMouseDown}
            >
              {layerMarquee && (
                <div
                  className="timeline-layer-marquee"
                  style={{
                    left: Math.min(layerMarquee.startX, layerMarquee.currentX),
                    top: Math.min(layerMarquee.startY, layerMarquee.currentY),
                    width: Math.abs(layerMarquee.currentX - layerMarquee.startX),
                    height: Math.abs(layerMarquee.currentY - layerMarquee.startY),
                  }}
                />
              )}
              {filteredTreeNodes.length === 0 ? (
                <div className="timeline-empty">
                  {showAnimatedOnly ? "No animated layers" : "No layers"}
                </div>
              ) : (
                <DndContext
                  sensors={layerSortSensors}
                  collisionDetection={layerDropCollisionDetection}
                  modifiers={[restrictToVerticalAxis]}
                  onDragStart={handleLayerSortDragStart}
                  onDragMove={updateLayerDropIntent}
                  onDragOver={updateLayerDropIntent}
                  onDragCancel={handleLayerSortDragCancel}
                  onDragEnd={handleLayerSortDragEnd}
                >
                  <SortableContext
                    items={sortableLayerIds}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredTreeNodes.map((node) => {
                      const layer = node.type === "layer" ? node.layer : null;
                      const isSelected = layer
                        ? layerMatchesInspectorKeySet(
                            toLayerIdentity(layer),
                            inspectorSelectedKeys
                          )
                        : false;
                      const isHovered = layer
                        ? layerMatchesInspectorKeySet(
                            toLayerIdentity(layer),
                            visibleInspectorHoveredKeys
                          )
                        : false;
                      const sortable = sortableLayerIdSet.has(node.nodeKey);
                      const dropPosition =
                        layerDropIntent?.kind === "insert" &&
                        layerDropIntent.overId === node.nodeKey
                          ? layerDropIntent.placement
                          : undefined;
                      const isDropContainer =
                        node.type === "layer" &&
                        layerDropIntent?.containerNodeKey === node.nodeKey;
                      const isDropInside =
                        node.type === "layer" &&
                        layerDropIntent?.kind === "inside" &&
                        layerDropIntent.overId === node.nodeKey;
                      return (
                        <TimelineLabelRow
                          key={node.nodeKey}
                          node={node}
                          connectionMap={connectionMap}
                          isPrecompExpanded={isPrecompExpanded}
                          isSelected={isSelected}
                          isHovered={isHovered}
                          isRowHovered={visibleHoveredNodeKey === node.nodeKey}
                          onLayerClick={handleLayerClick}
                          onLayerContextMenu={handleLayerContextMenu}
                          onRowMouseEnter={handleRowMouseEnter}
                          onRowMouseLeave={handleRowMouseLeave}
                          sortable={sortable}
                          dropPosition={dropPosition}
                          isDropContainer={isDropContainer}
                          isDropInside={isDropInside}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          {/* Right side - Tracks area */}
          <div className="timeline-tracks-area" ref={tracksAreaRef}>
            <TimelineRuler
              totalFrames={totalFrames}
              frameRate={frameRate}
              compositionStart={compositionStart}
              onSeek={handleSeek}
              markers={compositionMarkers}
              onMarkerUpdate={handleUpdateMarker}
              onMarkerDelete={handleDeleteMarker}
            >
              <TimelinePlayhead
                compositionStart={compositionStart}
                totalFrames={totalFrames}
                onSeek={handleSeek}
                variant="head"
              />
            </TimelineRuler>
            <TimelinePlayhead
              compositionStart={compositionStart}
              totalFrames={totalFrames}
              onSeek={handleSeek}
              variant="line"
            />
            {/* Overlay to visually distinguish non-working time areas */}
            <TimelineWorkAreaOverlay totalFrames={totalFrames} />
            {/* Scene trim bar with draggable handle */}
            <TimelineSceneTrimBar
              compositionStart={compositionStart}
              totalFrames={totalFrames}
            />
            <div
              className={`timeline-tracks ${
                activeLayerDragId ? "timeline-tracks--layer-dragging" : ""
              }`}
              ref={tracksRef}
              onMouseDown={handleTracksMouseDown}
            >
              <div className="timeline-tracks__content">
                {keyframeMarquee && (
                  <div
                    className="timeline-keyframe-marquee"
                    style={{
                      left: Math.min(
                        keyframeMarquee.startX,
                        keyframeMarquee.currentX
                      ),
                      top: Math.min(
                        keyframeMarquee.startY,
                        keyframeMarquee.currentY
                      ),
                      width: Math.abs(
                        keyframeMarquee.currentX - keyframeMarquee.startX
                      ),
                      height: Math.abs(
                        keyframeMarquee.currentY - keyframeMarquee.startY
                      ),
                    }}
                  />
                )}
                {filteredTreeNodes.map((node) => {
                  const layer = node.type === "layer" ? node.layer : null;
                  const isSelected = layer
                    ? layerMatchesInspectorKeySet(
                        toLayerIdentity(layer),
                        inspectorSelectedKeys
                      )
                    : false;
                  const isHovered = layer
                    ? layerMatchesInspectorKeySet(
                        toLayerIdentity(layer),
                        visibleInspectorHoveredKeys
                      )
                    : false;
                  return (
                    <TimelineTrackRow
                      key={node.nodeKey}
                      node={node}
                      compositionStart={compositionStart}
                      compositionEnd={compositionEnd}
                      totalFrames={totalFrames}
                      isSelected={isSelected}
                      isHovered={isHovered}
                      isRowHovered={visibleHoveredNodeKey === node.nodeKey}
                      onLayerClick={handleLayerClick}
                      onLayerContextMenu={handleLayerContextMenu}
                      onKeyframeClick={handleKeyframeClick}
                      onRowMouseEnter={handleRowMouseEnter}
                      onRowMouseLeave={handleRowMouseLeave}
                      selectedLayersWithTiming={selectedLayersWithTiming}
                      selectedKeyframeIds={selectedKeyframeIdSet}
                      clipToWorkarea={timelineStore.clipToWorkarea}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
